import { Injectable } from '@angular/core';
import { Buffer } from 'buffer/';

// Polyfill Buffer for the browser if not available
(window as any).Buffer = Buffer;

@Injectable({
  providedIn: 'root',
})
export class CryptoEngineService {
  public readonly encoder = new TextEncoder();
  public readonly decoder = new TextDecoder();

  // --- Dynamic BIP39 Loader ---
  private async getBip39(): Promise<any> {
    const bip39Module = await import('bip39');
    
    // 1. Check direct properties
    if (bip39Module && typeof (bip39Module as any).generateMnemonic === 'function') {
      return bip39Module;
    }
    
    // 2. Check first default wrapper
    const def = (bip39Module as any).default;
    if (def && typeof def.generateMnemonic === 'function') {
      return def;
    }
    
    // 3. Check double default wrapper (sometimes created by ESM/CJS interop)
    const doubleDef = def?.default;
    if (doubleDef && typeof doubleDef.generateMnemonic === 'function') {
      return doubleDef;
    }
    
    // 4. Fallback: if we still can't find it, return the best match or throw
    return def || bip39Module;
  }

  // --- Mnemonic ---

  async generateMnemonic(): Promise<string> {
    const bip39 = await this.getBip39();
    return bip39.generateMnemonic(256); // 24 words
  }

  async verifyMnemonic(mnemonic: string): Promise<{ masterKeyBytes: Uint8Array, searchSaltBytes: Uint8Array }> {
    const bip39 = await this.getBip39();
    if (!bip39.validateMnemonic(mnemonic)) {
      throw new Error('Invalid mnemonic');
    }
    const keys = await this.deriveKeysFromMnemonic(mnemonic);
    return keys;
  }

  async deriveKeysFromMnemonic(mnemonic: string): Promise<{
    masterKeyBytes: Uint8Array;
    searchSaltBytes: Uint8Array;
    combinedBytes: Uint8Array;
  }> {
    const bip39 = await this.getBip39();
    const seed = await bip39.mnemonicToSeed(mnemonic); // 512 bits (64 bytes)
    const buffer = new Uint8Array(seed);

    // First 32 bytes = Master Key (AES-GCM)
    const masterKeyBytes = buffer.slice(0, 32);
    // Next 32 bytes = Search Salt (HMAC)
    const searchSaltBytes = buffer.slice(32, 64);

    return {
      masterKeyBytes,
      searchSaltBytes,
      combinedBytes: buffer, // Full 64 bytes
    };
  }

  // --- Blind Indexing ---

  async generateBlindIndex(data: string, searchSalt: CryptoKey): Promise<string> {
    const normalized = data
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
    const encoded = this.encoder.encode(normalized);

    const signature = await window.crypto.subtle.sign(
      'HMAC',
      searchSalt,
      encoded,
    );

    return this.arrayBufferToHex(new Uint8Array(signature)); // Hex for DB consistency
  }

  // --- AES Wrap/Unwrap ---

  async deriveWrappingKey(
    pin: string,
    salt: ArrayBuffer,
  ): Promise<CryptoKey> {
    const pinKey = await window.crypto.subtle.importKey(
      'raw',
      this.encoder.encode(pin),
      { name: 'PBKDF2' },
      false,
      ['deriveKey'],
    );

    // Derive AES-KW Key
    return window.crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 100000,
        hash: 'SHA-256',
      },
      pinKey,
      { name: 'AES-KW', length: 256 },
      false,
      ['wrapKey', 'unwrapKey'],
    );
  }

  async importMasterKey(
    keyBytes: any,
    extractable: boolean,
  ): Promise<CryptoKey> {
    return window.crypto.subtle.importKey(
      'raw',
      keyBytes,
      { name: 'AES-GCM' },
      extractable,
      ['encrypt', 'decrypt'],
    );
  }

  async importSearchSalt(saltBytes: any): Promise<CryptoKey> {
    return window.crypto.subtle.importKey(
      'raw',
      saltBytes,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign'],
    );
  }

  // --- AES GCM Encrypt / Decrypt ---

  async encrypt(data: string, masterKey: CryptoKey): Promise<string> {
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const encodedData = this.encoder.encode(data);

    const ciphertext = await window.crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv },
      masterKey,
      encodedData,
    );
    // Prepend IV
    const resultBuffer = new Uint8Array(iv.byteLength + ciphertext.byteLength);
    resultBuffer.set(iv);
    resultBuffer.set(new Uint8Array(ciphertext), iv.byteLength);
    return this.arrayBufferToBase64(resultBuffer);
  }

  async decrypt(cipherBase64: string, masterKey: CryptoKey): Promise<string> {
    const resultBuffer = this.base64ToArrayBuffer(cipherBase64);
    const iv = resultBuffer.slice(0, 12);
    const ciphertext = resultBuffer.slice(12);

    const decryptedData = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv },
      masterKey,
      ciphertext,
    );

    return this.decoder.decode(decryptedData);
  }

  // --- Utilities ---

  arrayBufferToBase64(buffer: Uint8Array): string {
    let binary = '';
    const bytes = buffer;
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  arrayBufferToHex(buffer: Uint8Array): string {
    return Array.from(buffer)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  base64ToArrayBuffer(base64: string): Uint8Array {
    const binary_string = window.atob(base64);
    const len = binary_string.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary_string.charCodeAt(i);
    }
    return bytes;
  }
}
